import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Img, { ImgProps } from "@insite/mobius/Img";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
    image: ImageModel;
    useLargeImage?: boolean;
    onClick?: () => void;
    extendedStyles?: ProductPrimaryImageStyles;
    useMagnifier?: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

export interface ProductPrimaryImageStyles {
    centeringWrapper?: InjectableCss;
    img?: ImgProps;
}

export const productPrimaryImageStyles: ProductPrimaryImageStyles = {
    centeringWrapper: {
        css: css`
            min-height: 300px;
            margin: 10px 0;
            display: flex;
            align-items: center;
            justify-content: center;
            img {
                height: auto;
                max-width: 100%;
            }
        `,
    },
    img: {
        css: css`
            cursor: pointer;
        `,
    },
};

const ProductPrimaryImage = ({ productSettings, product, image, useLargeImage, onClick, extendedStyles, useMagnifier }: Props) => {
    const [styles] = useState(() => mergeToNew(productPrimaryImageStyles, extendedStyles));
    const [showMagnifier, setShowMagnifier] = useState(false);
    const [[x, y], setXY] = useState([0, 0]);
    const [[imgWidth, imgHeight], setSize] = useState([0, 0]);
    const [leftMagnifier, setLeftMagnifier] = useState(0);
    const [topMagnifier, setTopMagnifier] = useState(0);

    if (!product) {
        return null;
    }

    useEffect(() => {
        if ((window as any).sirvScriptAdded) {
            return;
        }

        if (
            productSettings.imageProvider !== "SIRV" ||
            !product.images ||
            product.images.every(o => o.imageType !== "360")
        ) {
            return;
        }

        const script = document.createElement("script");
        script.src = "https://scripts.sirv.com/sirv.js";
        script.async = true;

        document.body.appendChild(script);
        (window as any).sirvScriptAdded = true;
    }, [productSettings.imageProvider, product]);

    const imageWrapperClickHandler = () => {
        onClick?.();
    };

    const imageWrapperKeyHandler: React.KeyboardEventHandler<HTMLImageElement> = event => {
        event.preventDefault();
        event.stopPropagation();
        if (event.key === "Enter") {
            onClick?.();
        }
    };

    const onMouseEnter = (event: React.MouseEvent<HTMLImageElement>) => {
        if (!useMagnifier) {return;}

        // update image size and turn-on magnifier
        const elem = event.currentTarget;
        const domRectElem = elem.getBoundingClientRect();
        setSize([domRectElem.width, domRectElem.height]);
        
        // set left and top for magnifier
        if (elem.parentElement) {
            const domRectParent = elem.parentElement.getBoundingClientRect();
            setLeftMagnifier((domRectParent.width - domRectElem.width) / 2);
            setTopMagnifier((domRectParent.height - domRectElem.height) / 2);
        }
        setShowMagnifier(true);
    };

    const onMouseMove = (event: React.MouseEvent<HTMLImageElement>) => {
        if (!useMagnifier) {return;}

        // update cursor position
        const elem = event.currentTarget;
        const { top, left } = elem.getBoundingClientRect();

        // calculate cursor position on the image
        const x = event.pageX - left - window.pageXOffset;
        const y = event.pageY - top - window.pageYOffset;
        setXY([x, y]);
    };

    const onMouseLeave = () => {
        // close magnifier
        setShowMagnifier(false);
    };

    const zoomLevel = 2.5;
    const magnifierWidth = 150;
    const magnifierHeight = 150;
    const productHasImages = product.images!.length > 0;
    
    const path = useLargeImage
        ? image.largeImagePath || image.mediumImagePath
        : image.mediumImagePath || image.largeImagePath;
    if (!path) {
        return null;
    }


    return (
        <>
            {image.imageType === "Static" && (
                <StyledWrapper {...styles.centeringWrapper} 
                    onClick={imageWrapperClickHandler} 
                    style={{position: "relative"}}>
                    <Img
                        {...styles.img}
                        src={path}
                        altText={image.imageAltText}
                        loading="eager"
                        data-test-selector="productDetails_mainImage"
                        onKeyPress={imageWrapperKeyHandler}
                        tabIndex={0}
                        onMouseEnter={onMouseEnter}
                        onMouseMove={onMouseMove}
                        onMouseLeave={onMouseLeave}
                    />
                    {showMagnifier && productHasImages && (
                        <div
                            style={{
                                position: "absolute",
                                // prevent magnifier blocks the mousemove event of img
                                pointerEvents: "none",
                                
                                // set size of magnifier
                                height: `${magnifierHeight}px`,
                                width: `${magnifierWidth}px`,
                                
                                // move element center to cursor pos
                                top: `${y + topMagnifier - magnifierHeight / 2}px`,
                                left: `${x + leftMagnifier - magnifierWidth / 2}px`,
                                opacity: 1, // reduce opacity so you can verify position
                                border: "1px solid lightgray",
                                backgroundColor: "white",
                                backgroundImage: `url('${image.largeImagePath}')`,
                                backgroundRepeat: "no-repeat",
                        
                                // calculate zoomed image size
                                backgroundSize: `${imgWidth * zoomLevel}px ${imgHeight * zoomLevel}px`,
                        
                                // calculate position of zoomed image.
                                backgroundPositionX: `${-x * zoomLevel + magnifierWidth / 2}px`,
                                backgroundPositionY: `${-y * zoomLevel + magnifierHeight / 2}px`
                            }}
                        ></div>
                    )} 
                </StyledWrapper>
            )}
            {image.imageType === "360" && productSettings.imageProvider === "SIRV" && (
                <div style={{ minHeight: 300 }} onClick={imageWrapperClickHandler}>
                    <div className="Sirv" key={image.id} data-src={image.mediumImagePath} />
                </div>
            )}
        </>
    );
};

export default connect(mapStateToProps)(ProductPrimaryImage);
